import { useCallback, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Download, Loader2, Package } from 'lucide-react';
import { fetchAdminOrders, formatCents, type AdminOrderRow } from '@/lib/api';
import { downloadAuthenticatedCsv } from '@/lib/downloadAuthenticatedCsv';
import { downloadErrorMessage } from '@/lib/downloadAuthed';
import { ResponsiveTable, type Column } from '@/components/ui/ResponsiveTable';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './Orders.module.css';

interface OrdersPageProps {
  onNavigate: (path: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  paid: 'Paid',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

/** provisioning_state is a legacy name. 'pending' only means the order hands
 *  off to commissioning: commissioning never stamps the order, so a
 *  'Pending' label stayed on commissioned orders forever. 'provisioned'
 *  means the older provisioning flow created a tenant for it. Launch status
 *  lives on the deployment. The CSV keeps the provisioning_state key. */
const PROVISIONING_LABELS: Record<AdminOrderRow['provisioning_state'], string> = {
  not_required: '-',
  pending: 'Yes',
  provisioned: 'Legacy tenant',
};

/**
 * Admin store-orders list (store slice 4 client).
 *
 * Paginated React-Query list over /admin/orders with a status filter
 * and CSV export. keepPreviousData so page changes don't blank the
 * table while the next page loads — same shape as SessionsPage minus
 * the live-poll machinery (orders have no live signal to watch).
 *
 * Row click navigates to the order detail page, where the lifecycle
 * controls (mark processing / shipped / delivered) live.
 */
export function OrdersPage({ onNavigate }: OrdersPageProps) {
  useRecentRoute('Orders');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const listQuery = useQuery({
    queryKey: ['orders', 'list', { page, status: statusFilter }],
    queryFn: () =>
      fetchAdminOrders({
        page, limit: 25,
        status: statusFilter || undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const rows = listQuery.data?.data ?? [];
  const totalPages = listQuery.data?.meta.pages ?? 1;
  const total = listQuery.data?.meta.total ?? 0;
  const error = listQuery.error instanceof Error ? listQuery.error : null;

  const columns: Column<AdminOrderRow>[] = [
    {
      key: 'order',
      header: 'Order',
      mobile: 'primary',
      cell: (o) => <span className={styles.orderNumber}>{o.order_number}</span>,
    },
    {
      key: 'date',
      header: 'Date',
      mobile: 'secondary',
      cell: (o) => (
        <span className={styles.dimText}>
          {new Date(o.created_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'buyer',
      header: 'Buyer',
      mobile: 'body',
      cell: (o) => o.buyer_name ?? o.buyer_email ?? '-',
    },
    {
      key: 'total',
      header: 'Total',
      mobile: 'body',
      align: 'right',
      cell: (o) => <span className={styles.amount}>{formatCents(o.total_cents)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      mobile: 'body',
      cell: (o) => (
        <span className={`${styles.statusBadge} ${styles[`status_${o.status}`] ?? ''}`}>
          {STATUS_LABELS[o.status] ?? o.status}
        </span>
      ),
    },
    {
      key: 'sp',
      header: 'SP',
      mobile: 'hidden',
      cell: (o) => o.sales_partner_name ?? <span className={styles.dimText}>-</span>,
    },
    {
      key: 'provisioning',
      header: 'Commissioning handoff',
      mobile: 'body',
      cell: (o) =>
        o.provisioning_state === 'not_required'
          ? <span className={styles.dimText}>-</span>
          : PROVISIONING_LABELS[o.provisioning_state],
    },
  ];

  // CSV download — routes through the authenticated ky client via
  // downloadAuthenticatedCsv (an <a href download> can't carry the
  // Bearer token; see that module's header comment). Passes the
  // active status filter so the export matches what's on screen.
  // A failed export says why (29 Oct 2026), instead of failing silently.
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const handleDownloadCsv = useCallback(async () => {
    if (csvBusy) return;
    setCsvBusy(true);
    setCsvError(null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await downloadAuthenticatedCsv({
        path: 'admin/orders/export.csv',
        query: statusFilter ? { status: statusFilter } : undefined,
        fallbackFilename: `evi-orders-${today}.csv`,
      });
    } catch (err) {
      setCsvError(await downloadErrorMessage(err));
    } finally {
      setCsvBusy(false);
    }
  }, [csvBusy, statusFilter]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Package size={18} /></div>
          <div>
            <h1 className={styles.title}>Orders</h1>
            <p className={styles.subtitle}>{total} total</p>
          </div>
        </div>
        <button
          type="button"
          className={styles.csvBtn}
          onClick={handleDownloadCsv}
          disabled={csvBusy}
        >
          {csvBusy
            ? <Loader2 size={14} aria-hidden />
            : <Download size={14} />} Export CSV
        </button>
      </div>

      <div className={styles.filterRow}>
        <select
          className={styles.select}
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All statuses</option>
          <option value="paid">Paid</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {error && <div className={styles.error}>{error.message}</div>}
      {csvError && <div className={styles.error} role="alert">{csvError}</div>}

      {listQuery.isLoading ? (
        <p className={styles.loading}>Loading…</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={rows}
          rowKey={(o) => o.id}
          onRowClick={(o) => onNavigate(`/orders/${o.id}`)}
          emptyMessage="No orders found"
        />
      )}

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft size={16} /> Prev
          </button>
          <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
